/**
 * EmergencyNerveEase — 3-minute focused nerve-relief session for
 * acute post-op flare-ups.
 *
 * NOT the full 6-phase Post-Op journey. This is a single continuous
 * silver-blue "cool cloth" bathing the pinched nerve at L4–L5, held
 * for 180 seconds, with a slow breath cue and the currently-selected
 * Family Circle voice reading a soothing script.
 *
 * User comes to this in pain — the design is minimal, calm, one-tap.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, X, Waves } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const DURATION = 180; // 3 minutes

const authHeader = () => {
  const token = localStorage.getItem('reclaim_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const EmergencyNerveEase = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('entry'); // entry | playing | complete
  const [elapsed, setElapsed] = useState(0);
  const [breath, setBreath] = useState(0.5);
  const [affirmation, setAffirmation] = useState('');
  const [speakerName, setSpeakerName] = useState('');

  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const rafRef = useRef(null);
  const startRef = useRef(0);
  const affirmations = useRef([]);
  const affTimerRef = useRef(null);

  // Load emergency script (for affirmations) + voice URL.
  const start = useCallback(async () => {
    setMode('playing');

    try {
      // Load short affirmations
      const scriptRes = await fetch(`${API}/api/healing/spinal-ease/emergency-script`);
      const s = await scriptRes.json();
      affirmations.current = s.affirmations || [];

      // Load current voice config
      const cfgRes = await fetch(`${API}/api/healing/spinal-ease/voice-config-v2`, { headers: authHeader() });
      const cfg = cfgRes.ok ? await cfgRes.json() : null;
      setSpeakerName(cfg?.speaker_name || '');

      // Play voice at low volume — no music bed here, we want minimal sensory load.
      if (cfg?.voice_url) {
        const url = cfg.voice_url.startsWith('http') ? cfg.voice_url : `${API}${cfg.voice_url}`;
        audioRef.current = new Audio(url);
        audioRef.current.volume = 0.65;
        audioRef.current.play().catch(() => { /* autoplay may be blocked */ });
      }
    } catch { /* offline fallback: continue without voice */ }

    // Elapsed timer
    startRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const e = Math.floor((Date.now() - startRef.current) / 1000);
      setElapsed(e);
      if (e >= DURATION) {
        setMode('complete');
      }
    }, 1000);

    // Breath animation (~14s per cycle)
    const tick = () => {
      const t = (Date.now() - startRef.current) % 14000;
      setBreath(0.5 + 0.5 * Math.sin((t / 14000) * Math.PI * 2 - Math.PI / 2));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    // Cycle affirmations every ~9s.
    let idx = 0;
    const cycle = () => {
      if (affirmations.current.length === 0) return;
      setAffirmation(affirmations.current[idx % affirmations.current.length]);
      idx += 1;
    };
    cycle();
    affTimerRef.current = setInterval(cycle, 9000);
  }, []);

  const stop = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (affTimerRef.current) clearInterval(affTimerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
  }, []);

  // Cleanup on unmount + when 'complete' — but NOT every tick.
  // Bug fix (Feb 2026): previously included `elapsed` in the deps, which
  // caused this effect to re-run every second, tearing down the timers
  // and pausing the audio → the session appeared to freeze after a few
  // seconds. We now watch `mode` only.
  useEffect(() => {
    if (mode === 'complete') {
      stop();
      // Fire-and-forget session save.
      fetch(`${API}/api/healing/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: localStorage.getItem('userId') || 'unknown',
          condition: 'spinal-ease-emergency',
          duration_seconds: DURATION,
          steps_completed: ['emergency-nerve-ease'],
        }),
      }).catch(() => {});
    }
    return () => stop();
  }, [mode, stop]);

  const progress = elapsed / DURATION;

  return (
    <motion.div
      className="fixed inset-0 flex flex-col items-center justify-center overflow-hidden"
      style={{ background: 'radial-gradient(ellipse 100% 90% at 50% 45%, #0e1620 0%, #06090c 70%)' }}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      data-testid="emergency-nerve-ease"
    >
      {/* ─── ENTRY ─── */}
      <AnimatePresence>
        {mode === 'entry' && (
          <motion.div
            key="entry"
            className="absolute inset-0 flex flex-col items-center justify-center px-8 z-30"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <button
              onClick={() => navigate(-1)}
              className="absolute top-6 left-6 flex items-center gap-2 text-white/50 text-xs"
              style={{ fontFamily: "'Manrope', sans-serif" }}
              data-testid="emergency-back"
            >
              <ArrowLeft size={14} /> Back
            </button>

            <motion.div
              animate={{ scale: [1, 1.06, 1], opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              className="mb-8"
            >
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center"
                style={{
                  background: 'radial-gradient(circle, rgba(180,220,255,0.25) 0%, rgba(120,170,220,0.08) 60%, transparent 100%)',
                  border: '1px solid rgba(180,220,255,0.28)',
                }}
              >
                <Waves size={28} className="text-blue-200/80" />
              </div>
            </motion.div>

            <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Flare-Up Support · 3 minutes
            </p>
            <h1 className="text-white/85 text-3xl md:text-4xl mb-4 text-center" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
              Emergency Nerve Ease
            </h1>
            <p className="text-white/45 text-sm text-center max-w-sm leading-relaxed mb-10" style={{ fontFamily: "'Manrope', sans-serif" }}>
              A cool silver-blue light will settle around the nerve. Just breathe with it. This wave is already passing.
            </p>

            <motion.button
              onClick={start}
              whileTap={{ scale: 0.96 }}
              className="px-8 py-3.5 rounded-full text-sm"
              style={{
                background: 'rgba(180,220,255,0.14)',
                border: '1px solid rgba(180,220,255,0.35)',
                color: 'rgba(240,250,255,0.95)',
                fontFamily: "'Manrope', sans-serif",
                letterSpacing: '0.05em',
              }}
              data-testid="emergency-begin"
            >
              Begin
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── PLAYING ─── */}
      <AnimatePresence>
        {mode === 'playing' && (
          <>
            {/* Close */}
            <button
              onClick={() => navigate(-1)}
              className="absolute top-6 right-6 z-40 text-white/40 hover:text-white/80"
              data-testid="emergency-close"
            >
              <X size={20} />
            </button>

            {/* Progress bar (super subtle) */}
            <div className="absolute top-0 left-0 right-0 h-[2px] z-40">
              <div
                className="h-full transition-all"
                style={{
                  width: `${progress * 100}%`,
                  background: 'linear-gradient(90deg, rgba(180,220,255,0.6), rgba(160,200,240,0.4))',
                }}
              />
            </div>

            {/* Nerve visual — pulsing silver-blue orb */}
            <motion.div
              key="orb"
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 2 }}
            >
              {/* Outer nerve halo */}
              <motion.div
                animate={{
                  scale: 1 + breath * 0.15,
                  opacity: 0.35 + breath * 0.2,
                }}
                transition={{ duration: 0.6, ease: 'easeInOut' }}
                style={{
                  width: 460,
                  height: 460,
                  background: 'radial-gradient(circle, rgba(180,220,255,0.35) 0%, rgba(120,180,220,0.1) 40%, transparent 65%)',
                  filter: 'blur(30px)',
                  borderRadius: '50%',
                }}
              />
              {/* Nerve core */}
              <motion.div
                animate={{ scale: 1 + breath * 0.08 }}
                transition={{ duration: 0.6 }}
                className="absolute"
                style={{
                  width: 220,
                  height: 220,
                  background: 'radial-gradient(circle, rgba(230,245,255,0.95) 0%, rgba(160,210,240,0.55) 45%, rgba(80,130,180,0.25) 70%, transparent 90%)',
                  borderRadius: '50%',
                  boxShadow: '0 0 60px rgba(180,220,255,0.4)',
                }}
              />
              {/* Silver-blue outer ring */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 60, ease: 'linear', repeat: Infinity }}
                className="absolute"
                style={{
                  width: 320,
                  height: 320,
                  border: '1px solid rgba(180,220,255,0.15)',
                  borderRadius: '50%',
                }}
              />
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 45, ease: 'linear', repeat: Infinity }}
                className="absolute"
                style={{
                  width: 380,
                  height: 380,
                  border: '1px dashed rgba(180,220,255,0.08)',
                  borderRadius: '50%',
                }}
              />
            </motion.div>

            {/* Breath cue */}
            <motion.p
              className="absolute z-20 text-white/45 text-sm tracking-[0.4em] uppercase"
              style={{
                fontFamily: "'Manrope', sans-serif",
                bottom: '30%',
              }}
              animate={{ opacity: 0.3 + breath * 0.4 }}
              data-testid="emergency-breath-cue"
            >
              {breath > 0.6 ? 'breathe in' : 'breathe out'}
            </motion.p>

            {/* Rotating affirmation */}
            <div className="absolute z-20 px-8 text-center" style={{ bottom: '18%', maxWidth: 440 }}>
              <AnimatePresence mode="wait">
                <motion.p
                  key={affirmation}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 0.75, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 1.2 }}
                  className="text-white/70 text-lg leading-relaxed"
                  style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
                  data-testid="emergency-affirmation"
                >
                  {affirmation}
                </motion.p>
              </AnimatePresence>
            </div>

            {/* Speaker name */}
            {speakerName && (
              <p className="absolute bottom-6 left-6 text-white/25 text-[10px] tracking-[0.2em] uppercase z-20" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {speakerName}
              </p>
            )}
            {/* Time remaining */}
            <p className="absolute bottom-6 right-6 text-white/25 text-[10px] tracking-[0.2em] uppercase z-20" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {Math.max(0, DURATION - elapsed)}s
            </p>
          </>
        )}
      </AnimatePresence>

      {/* ─── COMPLETE ─── */}
      <AnimatePresence>
        {mode === 'complete' && (
          <motion.div
            key="complete"
            className="absolute inset-0 flex flex-col items-center justify-center px-8 z-30"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <p className="text-white/25 text-[10px] tracking-[0.3em] uppercase mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Complete
            </p>
            <h2 className="text-white/80 text-2xl mb-3 text-center" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
              The wave has passed
            </h2>
            <p className="text-white/40 text-sm text-center mb-10 max-w-xs" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Rest here for a moment. You can return whenever you need.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setElapsed(0); setMode('entry'); }}
                className="px-6 py-2.5 rounded-full text-xs text-white/45"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  fontFamily: "'Manrope', sans-serif",
                }}
                data-testid="emergency-again"
              >
                Once more
              </button>
              <button
                onClick={() => navigate('/healing')}
                className="px-6 py-2.5 rounded-full text-xs text-white/75"
                style={{
                  background: 'rgba(180,220,255,0.12)',
                  border: '1px solid rgba(180,220,255,0.28)',
                  fontFamily: "'Manrope', sans-serif",
                }}
                data-testid="emergency-done"
              >
                Return
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default EmergencyNerveEase;
